import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import SmoothScroll, { lenisInstance } from "@/lib/SmoothScroll";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Protected from "@/components/Protected";
import Home from "@/pages/Home";
import SitesPage from "@/pages/SitesPage";
import ArtistsPage from "@/pages/ArtistsPage";
import ParcoursPage from "@/pages/ParcoursPage";
import PartnersPage from "@/pages/PartnersPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import MemberPage from "@/pages/MemberPage";
import AdminPage from "@/pages/AdminPage";
import { AdminArtistsList, AdminArtistEdit } from "@/pages/AdminArtists";
import ArtistProfile from "@/pages/ArtistProfile";
import RiquetPage from "@/pages/RiquetPage";
import RecitPage from "@/pages/RecitPage";
import RejoindrePage from "@/pages/RejoindrePage";
import SiteDetailPage from "@/pages/SiteDetailPage";
import MecenatPage from "@/pages/MecenatPage";
import CarnetListPage from "@/pages/CarnetListPage";
import CarnetPostPage from "@/pages/CarnetPostPage";
import QuiSommesNousPage from "@/pages/QuiSommesNousPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentResultPage";
import PageTracker from "@/components/PageTracker";
import DonorsWallPage from "@/pages/DonorsWallPage";
import BoutiquePage from "@/pages/BoutiquePage";
import "@/App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Lenis hijacks window.scrollTo — use its own API for a deterministic reset
    // so IntersectionObserver / Framer's whileInView fire on the new page.
    if (lenisInstance) {
      lenisInstance.scrollTo(0, { immediate: true, force: true });
    }
    // Native fallback (also covers the very first mount before Lenis is ready)
    window.scrollTo(0, 0);
    // Nudge Framer/IO: some browsers only re-evaluate after a layout tick
    const t = setTimeout(() => window.dispatchEvent(new Event("scroll")), 30);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const hideChrome = pathname === "/login";
  return (
    <>
      <ScrollToTop />
      <PageTracker />
      {!hideChrome && <Nav />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/riquet" element={<RiquetPage />} />
        <Route path="/recit" element={<RecitPage />} />
        <Route path="/sites" element={<Navigate to="/parcours#parcours-2026" replace />} />
        <Route path="/sites/:slug" element={<SiteDetailPage />} />
        <Route path="/artistes" element={<ArtistsPage />} />
        <Route path="/artistes/:slug" element={<ArtistProfile />} />
        <Route path="/parcours" element={<ParcoursPage />} />
        <Route path="/partenaires" element={<PartnersPage />} />
        <Route path="/mecenat" element={<MecenatPage />} />
        <Route path="/carnet" element={<CarnetListPage />} />
        <Route path="/carnet/:slug" element={<CarnetPostPage />} />
        <Route path="/rejoindre" element={<RejoindrePage />} />
        <Route path="/qui-sommes-nous" element={<QuiSommesNousPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        <Route path="/donateurs" element={<DonorsWallPage />} />
        <Route path="/boutique" element={<BoutiquePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/espace" element={<Protected><MemberPage /></Protected>} />
        <Route path="/admin" element={<Protected admin><AdminPage /></Protected>} />
        <Route path="/admin/artistes" element={<Protected admin><AdminArtistsList /></Protected>} />
        <Route path="/admin/artistes/:id" element={<Protected admin><AdminArtistEdit /></Protected>} />
      </Routes>
      {!hideChrome && <Footer />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll>
            <div className="min-h-screen bg-bg text-fg">
              <Shell />
              <Toaster position="bottom-right" theme="dark" />
            </div>
          </SmoothScroll>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
